import React, { useState, useMemo, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useStore } from "../context/StoreContext";
import { t } from "../lib/i18n";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { CreditCard, Smartphone, Wallet, Banknote, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const paymentOptions = [
  { id: "mada", labelKey: "pay_mada", icon: CreditCard, colorClass: "text-emerald-700" },
  { id: "apple_pay", labelKey: "pay_apple", icon: Smartphone, colorClass: "text-black" },
  { id: "card", labelKey: "pay_card", icon: Wallet, colorClass: "text-indigo-700" },
  { id: "cod", labelKey: "pay_cod", icon: Banknote, colorClass: "text-amber-700" },
];

export default function Checkout() {
  const { cart, lang, user, config, currency, setCurrency, formatPrice, clearCart } = useStore();
  const location = useLocation();
  const navigate = useNavigate();
  const consolidate = location.state?.consolidate ?? true;

  const [country, setCountry] = useState("SA");
  const [payment, setPayment] = useState("mada");
  const [form, setForm] = useState({
    name: user?.name || "",
    phone: "",
    city: "",
    address: "",
  });
  const [placedOrder, setPlacedOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!config) return;
    const c = config.countries.find((x) => x.code === country);
    if (c) setCurrency(c.currency);
  }, [country, config]);

  const totals = useMemo(() => {
    const subtotal = cart.reduce((s, i) => s + i.product.price_usd * i.qty, 0);
    const countryRow = config?.countries.find((c) => c.code === country);
    const customsRate = countryRow?.customs_rate ?? 0.15;
    const taxRate = countryRow?.tax_rate ?? 0.05;
    const shippingBase = countryRow?.shipping_usd ?? 25;
    const shipping = consolidate ? shippingBase : shippingBase + (cart.length - 1) * 6;
    const customs = subtotal * customsRate;
    const tax = subtotal * taxRate;
    const total = subtotal + customs + tax + shipping;
    return { subtotal, customs, tax, shipping, total };
  }, [cart, config, country, consolidate]);

  const submitOrder = async () => {
    if (!form.name || !form.phone || !form.address) return toast.error(lang === "ar" ? "يرجى تعبئة كل الحقول" : "Please fill all fields");
    if (cart.length === 0) return;
    setLoading(true);
    try {
      const payload = {
        items: cart.map((i) => ({ product_id: i.id, qty: i.qty })),
        country,
        currency,
        payment_method: payment,
        consolidate_shipment: consolidate,
        address: { line: form.address, city: form.city },
        phone: form.phone,
        customer_name: form.name,
        subtotal_usd: totals.subtotal,
        customs_usd: totals.customs,
        tax_usd: totals.tax,
        shipping_usd: totals.shipping,
        total_usd: totals.total,
      };
      const { data } = await api.post("/orders", payload);
      setPlacedOrder(data);
      clearCart();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  if (placedOrder) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="order-success">
        <CheckCircle2 size={72} className="mx-auto mb-6 text-emerald-500" />
        <h1 className={`${lang === "ar" ? "font-arabic-heading" : "font-english-heading"} text-3xl md:text-4xl font-semibold mb-3`} style={{ color: "var(--thiqa-primary)" }}>
          {t(lang, "order_success")}
        </h1>
        <p className="text-gray-600 mb-2">{t(lang, "order_id")}: <span className="font-bold">{placedOrder.id}</span></p>
        <p className="text-gray-700 mb-8">{t(lang, "total")}: <span className="font-bold">{formatPrice(placedOrder.total_usd)}</span></p>
        <div className="flex gap-3 justify-center">
          <Button onClick={() => navigate("/orders")} className="btn-primary-thiqa rounded-full">{t(lang, "orders")}</Button>
          <Button variant="outline" onClick={() => navigate("/products")} className="rounded-full">{t(lang, "continue_shopping")}</Button>
        </div>
      </div>
    );
  }

  if (cart.length === 0) {
    return <div className="max-w-2xl mx-auto py-24 text-center text-gray-500">{t(lang, "empty_cart")}</div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-8 py-10 md:py-14">
      <h1 className={`${lang === "ar" ? "font-arabic-heading" : "font-english-heading"} text-4xl font-semibold mb-8`} style={{ color: "var(--thiqa-primary)" }}>
        {t(lang, "checkout")}
      </h1>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-[#e8ddc9] rounded-2xl p-6">
            <h3 className="font-semibold mb-4">{lang === "ar" ? "بيانات التوصيل" : "Delivery Details"}</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>{t(lang, "name")}</Label>
                <Input data-testid="checkout-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <Label>{t(lang, "phone")}</Label>
                <Input data-testid="checkout-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div>
                <Label>{t(lang, "country")}</Label>
                <select data-testid="checkout-country" value={country} onChange={(e) => setCountry(e.target.value)} className="w-full h-10 border border-input rounded-md px-3 bg-white">
                  {config?.countries.map((c) => <option key={c.code} value={c.code}>{lang === "ar" ? c.name_ar : c.name_en}</option>)}
                </select>
              </div>
              <div>
                <Label>{t(lang, "city")}</Label>
                <Input data-testid="checkout-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label>{t(lang, "address")}</Label>
                <Input data-testid="checkout-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              </div>
            </div>
          </div>

          <div className="bg-white border border-[#e8ddc9] rounded-2xl p-6">
            <h3 className="font-semibold mb-4">{t(lang, "payment_method")}</h3>
            <RadioGroup value={payment} onValueChange={setPayment} className="grid md:grid-cols-2 gap-3">
              {paymentOptions.map((p) => (
                <label key={p.id} data-testid={`pay-${p.id}`} className={`flex items-center gap-3 border rounded-xl p-4 cursor-pointer transition ${payment === p.id ? "border-[color:var(--thiqa-primary)] bg-[#faf3e3]" : "border-gray-200 hover:border-gray-300"}`}>
                  <RadioGroupItem value={p.id} id={p.id} />
                  <p.icon size={22} className={p.colorClass} />
                  <span className="font-medium text-sm">{t(lang, p.labelKey)}</span>
                </label>
              ))}
            </RadioGroup>
            <p className="text-xs text-gray-500 mt-3">
              {lang === "ar" ? "المعاملة تجريبية — لن يتم خصم أي مبلغ فعلي." : "Demo transaction — no real charges."}
            </p>
          </div>
        </div>

        <div className="bg-white border border-[#e8ddc9] rounded-2xl p-6 h-fit sticky top-24" data-testid="checkout-summary">
          <h3 className="font-semibold text-lg mb-4">{lang === "ar" ? "ملخص الطلب" : "Order Summary"}</h3>
          <div className="space-y-2 text-sm">
            <Row label={t(lang, "subtotal")} val={formatPrice(totals.subtotal)} />
            <Row label={t(lang, "customs")} val={formatPrice(totals.customs)} />
            <Row label={t(lang, "tax")} val={formatPrice(totals.tax)} />
            <Row label={t(lang, "shipping")} val={formatPrice(totals.shipping)} />
            <div className="border-t border-gray-200 pt-3 mt-3">
              <div className="flex justify-between text-lg font-bold" style={{ color: "var(--thiqa-primary)" }}>
                <span>{t(lang, "total")}</span>
                <span data-testid="total-amount">{formatPrice(totals.total)}</span>
              </div>
            </div>
          </div>
          <Button data-testid="place-order-btn" onClick={submitOrder} disabled={loading} className="w-full btn-primary-thiqa rounded-full py-6 mt-6">
            {loading ? "…" : t(lang, "place_order")}
          </Button>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, val }) => (
  <div className="flex justify-between">
    <span className="text-gray-600">{label}</span>
    <span className="font-medium">{val}</span>
  </div>
);
